import { Button } from '@chakra-ui/button';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import Icon from '@chakra-ui/icon';
import { Image } from '@chakra-ui/image';
import { Input } from '@chakra-ui/input';
import { Flex, Heading, Link, Text } from '@chakra-ui/layout';
import { useToast } from '@chakra-ui/toast';
import firebase from 'firebase/app';
import React, { useState } from 'react';
import { UserPlus } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import freesign from '../../../assets/images/freesign.png';
import { FirebaseConfig, UserMatchType } from '../../../types/types';
import { RegisterConsent } from './RegisterConsent';

interface CheckUserRegistrationProps {
  userMatch: (userMatch: UserMatchType) => void;
  firebaseConfig: FirebaseConfig;
}

type FormValues = { email: string };

export const CheckUserRegistration: React.FC<CheckUserRegistrationProps> = ({
  userMatch,
  firebaseConfig,
}) => {
  const { register, handleSubmit } = useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const toast = useToast();
  const history = useHistory();

  const onSubmit = handleSubmit(async ({ email }) => {
    setLoading(true);
    try {
      const isAlreadyUser = await firebase
        .auth()
        .fetchSignInMethodsForEmail(email);

      if (isAlreadyUser.length === 0) {
        await firebase
          .app()
          .functions(
            process.env.ENV === 'development' ? undefined : 'europe-west3'
          )
          .httpsCallable('checkUserStatus')({ email })
          .then((result) => {
            const data = result.data as UserMatchType;
            if (data && !data.error) {
              userMatch(data);
            } else {
              // eslint-disable-next-line no-console
              console.log(result.data.error);
              toast({
                title: 'Ein unbekannter Fehler ist aufgetreten',
                description: 'Versuche es später erneut.',
                status: 'error',
                duration: 9000,
                isClosable: true,
              });
            }
          });
      } else {
        history.push('/login');
        toast({
          title: 'Deine E-Mail ist bereits vergeben',
          description: 'Bitte logge dich hier auf unserer Loginseite ein.',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (e) {
      await firebase
        .firestore()
        .collection('errors')
        .add({ id: 'checkUserRegistration', error: JSON.stringify(e) });

      toast({
        title: 'Fehler',
        description:
          'Überprüfen SIe Ihre Internetverbindung. Falls diese vorhanden ist, geben Sie bitte dem Studio bescheid.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
    setLoading(false);
  });

  if (!hasConfirmed)
    return (
      <RegisterConsent
        hasDeclined={() => history.push('/login')}
        hasConfirmed={setHasConfirmed}
      />
    );

  return (
    <>
      <Flex direction="column" p={4} bg="gray.100" borderRadius="md">
        <Heading size="sm" mb={4}>
          Ich bin bereits im Studio angemeldet
        </Heading>
        <Text mb={1}>
          Super, dann kannst du unser Online-Angebot kostenfrei nutzen.
        </Text>
        <Text mb={1}>
          Gib einfach die E-Mail-Adresse ein, die du bei der
          <b> Anmeldung im Studio angegeben hast</b>.
        </Text>
        <Text>
          Falls du deine E-Mail nicht weißt, wende dich bitte an einem{' '}
          <Link
            color="brand.500"
            href={firebaseConfig?.contactUrl}
            target="_blank"
          >
            Mitarbeiter aus dem Studio.
          </Link>
        </Text>
      </Flex>
      <Text my={3} textAlign="center">
        - oder -
      </Text>
      <Flex />
      <Flex direction="column" p={4} bg="gray.100" borderRadius="md" mb={6}>
        <Heading size="sm" mb={4}>
          Ich bin neu hier
        </Heading>
        <Flex
          direction={['column', 'row', 'row']}
          justify="center"
          align="center"
        >
          <Image src={freesign} height="60px" width="60px" mr={3} />
          <Text>
            Wenn du noch nicht im Studio angemeldet bist, schenken wir dir{' '}
            <b>{firebaseConfig.trialExpiration} Tage unbegrenzten Zutritt</b> zu
            unserem Online-Angebot. Gib dazu einfach deine Lieblings-E-Mail an.
          </Text>
        </Flex>
      </Flex>

      <form onSubmit={onSubmit}>
        <FormControl isRequired={true}>
          <FormLabel>E-Mail</FormLabel>
          <Input
            autoComplete="email"
            autoFocus={true}
            focusBorderColor="brand.500"
            type="email"
            placeholder="jemand@jemand.de"
            {...register('email', { required: true })}
          />
        </FormControl>
        <Button
          focusBorderColor="brand.500"
          colorScheme="brand"
          width="full"
          mt={4}
          type="submit"
          isLoading={loading}
          leftIcon={<Icon as={UserPlus} />}
        >
          Registrieren
        </Button>
      </form>
    </>
  );
};
